/**
 * ZipcodeValidator - Enterprise-grade zipcode validation with cart management
 * Optimized for performance, maintainability, and user experience
 */
class ZipcodeValidator {
  static SELECTORS = {
    popup: '[data-zipcode-popup]',
    submitBtn: '[data-submit-zipcode]',
    zipcodeInput: '#zipcode',
    closeBtn: '[data-newsletter-close-btn]',
    cartCount: '[data-cart-count]'
  };

  static API_ENDPOINTS = {
    validate: '/apps/zip-code-validator/api/user-type',
    clearCart: '/cart/clear.js'
  };

  static MESSAGES = {
    empty: 'Please enter a zipcode to continue',
    validationFailed: 'Invalid zipcode. Please check and try again.',
    networkError: 'Unable to validate zipcode. Please check your connection and try again.',
    checking: 'Checking...',
    submit: 'Submit'
  };

  constructor() {
    this.popup = document.querySelector(ZipcodeValidator.SELECTORS.popup);
    if (!this.popup) return;

    this.submitBtn = this.popup.querySelector(ZipcodeValidator.SELECTORS.submitBtn);
    this.zipcodeInput = this.popup.querySelector(ZipcodeValidator.SELECTORS.zipcodeInput);
    this.isZipcodeRequired = false;
    
    this.init();
    this.updateHeaderZipcode();
    this.updateDetailBasedOnLocation();
  }

  init() {
    this.submitBtn?.addEventListener('click', this.handleSubmit.bind(this));
    this.setupCloseProtection();
  }

  setupCloseProtection() {
    // Allow close button to work, but block site interaction until zipcode is entered
    // Close button and ESC key will close the popup, but clicking elsewhere reopens it
    
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  handleOutsideClick(event) {
    // Skip if zipcode is already set
    if (localStorage.getItem('userZipcode')) return;
    
    // Skip if not required
    if (!this.isZipcodeRequired) return;
    
    // Allow clicks on the zipcode opener elements (including the icon) - let them open the popup
    const isClickOnOpener = event.target.closest('[data-modal-opener="PopupModal-zipcode"]') !== null;
    if (isClickOnOpener) return;
    
    // Allow clicks inside the popup
    if (this.popup && this.popup.contains(event.target)) return;
    
    // Allow clicks on the modal dialog overlay (to close) and close buttons
    const isClickOnCloseBtn = event.target.closest('[data-newsletter-close-btn]') !== null ||
                               event.target.closest('.btn-close') !== null ||
                               event.target.closest('.popup-close-btn') !== null;
    if (isClickOnCloseBtn) return;
    
    // Check if popup is currently open
    const modalDialog = document.querySelector('#PopupModal-zipcode');
    const isPopupOpen = modalDialog && modalDialog.querySelector('.modal.open');
    
    // If popup is closed and user clicks elsewhere, show notification and reopen popup
    if (!isPopupOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.showNotification('Zipcode Required', 'Please enter a valid zipcode to continue browsing.', 'info');
      // Reopen the popup
      this.openZipcodePopup();
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const zipcode = this.zipcodeInput.value.trim();
    if (!this.validateInput(zipcode)) return;
    await this.processZipcode(zipcode);
  }

  validateInput(zipcode) {
    if (!zipcode) {
      this.showNotification('Zipcode Required', ZipcodeValidator.MESSAGES.empty, 'info');
      return false;
    }
    return true;
  }

  async processZipcode(zipcode) {
    this.setLoadingState(true);

    try {
      const userType = await this.validateZipcode(zipcode);
      await this.handleValidationSuccess(userType, zipcode);
      this.closePopup();
    } catch (error) {
      this.handleValidationError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  async validateZipcode(zipcode) {
    const response = await fetch("/apps/zip-code-validator/api/user-type", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        postal_code: zipcode
      })
    });

    if (!response.ok) {
      throw new Error('Validation failed');
    }

    const data = await response.json();
    
    return data.data.userType;
  }

  async handleValidationSuccess(userType, zipcode) {
    localStorage.setItem('userLocation', userType);
    localStorage.setItem('userZipcode', zipcode);
    
    // Update header zipcode display
    this.updateHeaderZipcode();
    
    // Update buy buttons based on location
    this.updateBuyButtons(userType);
    
    if (userType === 'p1') {
      await this.clearCart();
      this.showNotification('Success', 'Zipcode validated successfully!', 'success');
    } else {
      this.showNotification('Success', 'Zipcode validated successfully!', 'success');
    }
    this.updateDetailBasedOnLocation();
  }

  async clearCart() {
    try {
      const response = await fetch(ZipcodeValidator.API_ENDPOINTS.clearCart, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        this.updateCartDisplay();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        
        // Update cart drawer with fresh data
        this.getCartData();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  updateCartDisplay() {
    document.querySelectorAll(ZipcodeValidator.SELECTORS.cartCount)
      .forEach(el => el.textContent = '0');
  }

  getCartData() {
    try {
      // Use existing cart-form component's getCartData method
      const cartForm = document.querySelector('cart-form');
      if (cartForm && typeof cartForm.getCartData === 'function') {
        cartForm.getCartData();
      } else {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (error) {
      // Silent error handling
    }
  }

  closePopup() {
    this.isZipcodeRequired = false; // Allow closing after successful validation
    document.querySelector(ZipcodeValidator.SELECTORS.closeBtn)?.click();
  }

  handleValidationError(error) {
    const isValidationError = error.message === 'Validation failed';
    const title = isValidationError ? 'Zipcode Validation' : 'Connection Error';
    const message = isValidationError 
      ? ZipcodeValidator.MESSAGES.validationFailed 
      : ZipcodeValidator.MESSAGES.networkError;
    
    this.showNotification(title, message, 'error');
  }

  setLoadingState(isLoading) {
    if (!this.submitBtn) return;
    
    this.submitBtn.disabled = isLoading;
    this.submitBtn.textContent = isLoading 
      ? ZipcodeValidator.MESSAGES.checking 
      : ZipcodeValidator.MESSAGES.submit;
  }

  showNotification(title, message, type = 'info') {
    if (window.notificationEle?.updateNotification) {
      window.notificationEle.updateNotification(title, message, {
        type,
        timeout: 5000
      });
    } else {
      // Fallback if notification system not available
    }
  }

  updateHeaderZipcode() {
    const savedZipcode = localStorage.getItem('userZipcode');
    const savedLocation = localStorage.getItem('userLocation');
    
    if (savedZipcode) {
      const headerZipcodeElements = document.querySelectorAll('.header-zipcode');
      headerZipcodeElements.forEach(element => {
        // Replace static zipcode with the stored one from localStorage
        element.textContent = savedZipcode;
      });
      
      // Update buy buttons if location is available
      if (savedLocation) {
        this.updateBuyButtons(savedLocation);
      }
    } else {
      this.openZipcodePopup();
    }
  }

  updateBuyButtons(userType) {
    const costcoButtons = document.querySelectorAll('[costco-product-url]');
    const atcButtons = document.querySelectorAll('[data-atc-button-pdp]');
    
    // Check if buttons exist before proceeding
    if (costcoButtons.length === 0 && atcButtons.length === 0) {
      return;
    }
    
    if (userType === 'p1') {
      // P1: Show Costco button, hide Add to Cart button
      if (costcoButtons.length > 0) {
        costcoButtons.forEach(btn => {
          btn.classList.remove('d-none');
        });
      }
      
      if (atcButtons.length > 0) {
        atcButtons.forEach(btn => {
          btn.classList.add('d-none');
        });
      }
      
    } else if (userType === 'p2') {
      // P2: Hide Costco button, show Add to Cart button
      if (costcoButtons.length > 0) {
        costcoButtons.forEach(btn => {
          btn.classList.add('d-none');
        });
      }
      
      if (atcButtons.length > 0) {
        atcButtons.forEach(btn => {
          btn.classList.remove('d-none');
        });
      }
    }
  }

  openZipcodePopup() {
    const zipcodeModalOpener = document.querySelector('[data-modal-opener="PopupModal-zipcode"]');
    if (zipcodeModalOpener) {
      this.isZipcodeRequired = true; // Mark as required to prevent closing
      zipcodeModalOpener.click();
    }
  }

  updateDetailBasedOnLocation(){
    const userLocation = localStorage.getItem('userLocation');
    if(userLocation === 'p1'){
     document.querySelectorAll("[data-location='p1']").forEach(el => el.classList.remove('d-none'));
     document.querySelectorAll("[data-location='p2']").forEach(el => el.classList.add('d-none'));
    }else {
      document.querySelectorAll("[data-location='p1']").forEach(el => el.classList.add('d-none'));
      document.querySelectorAll("[data-location='p2']").forEach(el => el.classList.remove('d-none'));
    }
  }
}

// Initialize when DOM is ready
document.readyState === 'loading' 
  ? document.addEventListener('DOMContentLoaded', () => new ZipcodeValidator())
  : new ZipcodeValidator();